// src/components/geral/MonitorImpressaoBackground.js
import { useEffect } from "react";

import { usePrinter } from "../../contexts/geral/PrinterContext";
import { useToken } from "../../contexts/geral/TokenContext";
import { usePedido } from "../../contexts/pedidos/PedidoContext";
import { usePrinterComanda } from "../../contexts/geral/PrinterComandaContext";
import { useSSE } from "../../contexts/geral/SSEContext";
import { useConfigModulo } from "../../contexts/administrativo/ConfigModuloContext";

const pedidosJaImpressos = new Set();
const comandasJaImpressas = new Set();
const notasJaImpressas = new Set();
const notasServicoJaImpressas = new Set();
const recibosJaImpressos = new Set();
const comprovantesJaImpressos = new Set();
const impressoesSsePendentes = new Set();
let filaImpressaoSse = Promise.resolve();

const enfileirarImpressaoSse = (chave, tarefa) => {
  if (impressoesSsePendentes.has(chave)) return;

  impressoesSsePendentes.add(chave);
  filaImpressaoSse = filaImpressaoSse
    .catch(() => undefined)
    .then(tarefa)
    .catch((error) => {
      console.error(`Erro ao processar a fila de impressão SSE (${chave}):`, error);
    })
    .finally(() => impressoesSsePendentes.delete(chave));
};

const MonitorImpressaoBackground = () => {
  const { printPedidoPDFById, printNFCePDFById, printNFSePDFById, printReciboPDF, imprimirComprovantePagamento } = usePrinter();
  const { imprimirItensComandaSemPedido, ComandaPDFPorPedidoId } = usePrinterComanda();
  const { getPedidoById } = usePedido();
  const { token } = useToken();
  const { subscribeToEvent } = useSSE();
  const { configModuloSel } = useConfigModulo();

  useEffect(() => {
    if (!token || token === "null") return undefined;

    const unsubscribePedido = subscribeToEvent("pedido", (event) => {
      try {
        const data = JSON.parse(event.data);
        const { pedidoId, imprimir } = data;

        if (!pedidoId) return;
        if (imprimir === false) return;
        if (pedidosJaImpressos.has(pedidoId)) return;

        enfileirarImpressaoSse(`pedido|${pedidoId}`, async () => {
          if (pedidosJaImpressos.has(pedidoId)) return;

          const pedido = await getPedidoById(pedidoId);
          if (!pedido) return;

          if (pedido.avulso) {
            //const imprimirPedidoAvulso = configModuloSel?.imprimirPedidoAvulso === true || configModuloSel?.imprimirPedidoAvulso === 1;
            if (!configModuloSel?.imprimirPedidoAvulso) {
              pedidosJaImpressos.add(pedidoId);
              return;
            }

            // await imprimirComandaPorPedido(pedido);
            await ComandaPDFPorPedidoId(pedidoId);
          } else {
            //printReciboPedido(pedido);
            await printPedidoPDFById(pedidoId);
          }

          pedidosJaImpressos.add(pedidoId);
        });
      } catch (err) {
        console.error("Erro ao processar pedido SSE para impressao:", err);
      }
    });

    const unsubscribeComanda = subscribeToEvent("comanda", (event) => {
      try {
        let comandaId;
        let itemId = 0;

        if (event.data.startsWith("{")) {
          const data = JSON.parse(event.data);
          comandaId = data.comandaId;
          itemId = data.itemId ?? 0;
        } else {
          [comandaId, itemId] = event.data.split("|").map(Number);
        }

        if (!comandaId) return;

        const key = `${comandaId}|${itemId}`;
        if (comandasJaImpressas.has(key)) return;

        enfileirarImpressaoSse(`comanda|${key}`, async () => {
          if (comandasJaImpressas.has(key)) return;
          await imprimirItensComandaSemPedido(comandaId, itemId);
          comandasJaImpressas.add(key);
        });
      } catch (err) {
        console.error("Erro ao imprimir comanda via SSE:", err, event.data);
      }
    });

    const unsubscribeNota = subscribeToEvent("nota", (event) => {
      try {
        const data = JSON.parse(event.data);
        const { notaId, situacaoNF } = data;

        if (!notaId) return;
        if (Number(situacaoNF) !== 2) return;

        const key = `${notaId}|${situacaoNF}`;
        if (notasJaImpressas.has(key)) return;

        enfileirarImpressaoSse(`nota|${key}`, async () => {
          if (notasJaImpressas.has(key)) return;
          await printNFCePDFById(notaId, 75, true);
          notasJaImpressas.add(key);
        });
      } catch (err) {
        console.error("Erro ao imprimir nota via SSE:", err, event.data);
      }
    });

    const unsubscribeNFSe = subscribeToEvent("nfse", (event) => {
      try {
        const { notaId } = JSON.parse(event.data);
        if (!notaId || notasServicoJaImpressas.has(notaId)) return;

        enfileirarImpressaoSse(`nfse|${notaId}`, async () => {
          if (notasServicoJaImpressas.has(notaId)) return;
          await printNFSePDFById(notaId, 75, true);
          notasServicoJaImpressas.add(notaId);
        });
      } catch (err) {
        console.error("Erro ao imprimir NFS-e via SSE:", err, event.data);
      }
    });

    const unsubscribeRecibo = subscribeToEvent("recibo", (event) => {
      try {
        const { notaId } = JSON.parse(event.data);
        if (!notaId || recibosJaImpressos.has(notaId)) return;

        enfileirarImpressaoSse(`recibo|${notaId}`, async () => {
          if (recibosJaImpressos.has(notaId)) return;
          const impresso = await printReciboPDF(notaId, 75);
          if (impresso) {
            recibosJaImpressos.add(notaId);
          }
        });
      } catch (err) {
        console.error("Erro ao imprimir recibo via SSE:", err, event.data);
      }
    });

    const unsubscribeComprovante = subscribeToEvent('comprovante-pag-comanda', (event) => {
      try {
        if (!configModuloSel?.monitorWeb || configModuloSel?.monitorDesktop || !configModuloSel?.comprovantePagComanda) return;
        const { comandaId, fechamento, filialId } = JSON.parse(event.data);
        if (!comandaId || !fechamento || Number(filialId) !== Number(configModuloSel.filialId)) return;
        const chave = `comprovante|${comandaId}|${fechamento}`;
        enfileirarImpressaoSse(chave, async () => {
          if (comprovantesJaImpressos.has(chave)) return;
          if (await imprimirComprovantePagamento({ id: comandaId, fechamento })) {
            comprovantesJaImpressos.add(chave);
          }
        });
      } catch (error) {
        console.error('Erro ao imprimir comprovante de pagamento via SSE:', error);
      }
    });

    return () => {
      unsubscribeComprovante();
      unsubscribePedido();
      unsubscribeComanda();
      unsubscribeNota();
      unsubscribeNFSe();
      unsubscribeRecibo();
    };
  }, [token, getPedidoById, printPedidoPDFById, printNFCePDFById, printNFSePDFById, printReciboPDF, imprimirItensComandaSemPedido, ComandaPDFPorPedidoId, subscribeToEvent, configModuloSel, imprimirComprovantePagamento]);

  return null;
};

export default MonitorImpressaoBackground;
